import { useState, useCallback, useMemo } from 'react'
import { AbsenceTypesEnum } from '../../Absence/types'

export interface UseManAuthValidationParams {
  isManAuthOn: boolean
  absenceCategoryId?: number
  absenceTypeId?: number
  absenceReasonValue?: number
  absenceTypeValue?: number
  exceededHours: number
  toWorkBack?: boolean
  hasWorkBackDays?: boolean
  discretionaryPayReasons: number[]
  externalManagerApprovedHours?: number
  onManagerApprovedHoursChange?: (value: number | undefined) => void
}

export interface UseManAuthValidationReturn {
  // State
  isPayApprovalConfirmed: boolean
  managerApprovedHours: number | undefined
  hasValidHoursInput: boolean
  
  // Computed
  isManAuthRequest: boolean
  isManAuthValid: boolean
  isManAuthValidWithHours: boolean
  
  // Handlers
  handleApprovalHoursChange: (value: number | null) => void
  handlePayApprovalChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  setIsPayApprovalConfirmed: (value: boolean) => void
}

/**
 * Custom hook to manage Manager Authorization (Man Auth) validation and state
 * Handles approval hours input, payment confirmation, and validation logic
 */
export const useManAuthValidation = ({
  isManAuthOn,
  absenceCategoryId,
  absenceTypeId,
  absenceReasonValue,
  absenceTypeValue,
  exceededHours,
  toWorkBack = false,
  hasWorkBackDays = false,
  discretionaryPayReasons,
  externalManagerApprovedHours,
  onManagerApprovedHoursChange,
}: UseManAuthValidationParams): UseManAuthValidationReturn => {
  const [isPayApprovalConfirmed, setIsPayApprovalConfirmed] = useState<boolean>(false)
  const [internalManagerApprovedHours, setInternalManagerApprovedHours] = useState<number | undefined>(undefined)
  const [hasValidHoursInput, setHasValidHoursInput] = useState<boolean>(false)

  const usingExternalState = onManagerApprovedHoursChange !== undefined
  const managerApprovedHours = usingExternalState
    ? externalManagerApprovedHours
    : internalManagerApprovedHours

  /**
   * Handles changes to the approval hours input
   */
  const handleApprovalHoursChange = useCallback((value: number | null) => {
    const next = value === null ? undefined : value
    setHasValidHoursInput(value !== null)
    if (onManagerApprovedHoursChange) {
      onManagerApprovedHoursChange(next)
    } else {
      setInternalManagerApprovedHours(next)
    }
  }, [onManagerApprovedHoursChange])

  /**
   * Handles changes to the payment approval checkbox
   */
  const handlePayApprovalChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setIsPayApprovalConfirmed(event.target.checked)
  }, [])

  /**
   * Determines if this absence requires manager authorization
   * Man auth is required when:
   * 1. Man auth feature is enabled
   * 2. Either:
   *    a. Non-sickness absence with discretionary pay reason, OR
   *    b. Sickness absence with exceeded OSP hours
   * 3. Not a "to work back" scenario
   */
  const isManAuthRequest = useMemo(() => {
    if (!isManAuthOn) return false
    
    // For components using absenceReasonValue/absenceTypeValue (WfmAbsenceDrawer)
    if (absenceReasonValue !== undefined && absenceTypeValue !== undefined) {
      return (
        ((absenceReasonValue === AbsenceTypesEnum.NonSickness &&
          discretionaryPayReasons.some(x => x === absenceTypeValue)) ||
          (absenceReasonValue === AbsenceTypesEnum.Sickness && exceededHours > 0)) &&
        !toWorkBack
      )
    }
    
    // For components using absenceCategoryId/absenceTypeId (AbsenceDrawerComponent)
    if (absenceCategoryId !== undefined && absenceTypeId !== undefined) {
      return (
        ((absenceCategoryId === AbsenceTypesEnum.NonSickness &&
          discretionaryPayReasons.some(x => x === absenceTypeId)) ||
          (absenceCategoryId === AbsenceTypesEnum.Sickness && exceededHours > 0)) &&
        !toWorkBack &&
        !hasWorkBackDays
      )
    }
    
    return false
  }, [
    isManAuthOn,
    absenceCategoryId,
    absenceTypeId,
    absenceReasonValue,
    absenceTypeValue,
    exceededHours,
    toWorkBack,
    hasWorkBackDays,
    discretionaryPayReasons,
  ])

  /**
   * Validates that man auth request has valid hours input
   */
  const isManAuthValid = useMemo(
    () =>
      isManAuthRequest &&
      hasValidHoursInput &&
      managerApprovedHours !== undefined &&
      managerApprovedHours >= 0,
    [isManAuthRequest, hasValidHoursInput, managerApprovedHours]
  )

  /**
   * Validates that man auth request has valid hours and hours > 0
   */
  const isManAuthValidWithHours = useMemo(
    () => isManAuthValid && managerApprovedHours !== undefined && managerApprovedHours > 0,
    [isManAuthValid, managerApprovedHours]
  )

  return {
    // State
    isPayApprovalConfirmed,
    managerApprovedHours,
    hasValidHoursInput,
    
    // Computed
    isManAuthRequest,
    isManAuthValid,
    isManAuthValidWithHours,
    
    // Handlers
    handleApprovalHoursChange,
    handlePayApprovalChange,
    setIsPayApprovalConfirmed,
  }
}